<template>
  <div class="dashboard">
    <div class="page-header">
      <div>
        <h1 class="page-title">{{ greeting }}, {{ userName }}</h1>
        <p class="page-sub">{{ t('dashboard.sub') }}</p>
      </div>
      <button class="btn btn-primary" @click="showForm = true">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
        {{ t('dashboard.newTask') }}
      </button>
    </div>

    <!-- Stats -->
    <div class="stat-strip">
      <div class="stat">
        <div class="stat-value">{{ stats.open }}</div>
        <div class="stat-label">{{ t('dashboard.open') }}</div>
      </div>
      <div class="stat">
        <div class="stat-value">{{ stats.in_progress }}</div>
        <div class="stat-label">{{ t('dashboard.inProgress') }}</div>
      </div>
      <div class="stat">
        <div class="stat-value">{{ stats.done }}</div>
        <div class="stat-label">{{ t('dashboard.done') }}</div>
      </div>
      <div class="stat stat-crit">
        <div class="stat-value">{{ stats.critical }}</div>
        <div class="stat-label">{{ t('dashboard.critical') }}</div>
      </div>
    </div>

    <!-- Recent Tasks -->
    <div class="section-header">
      <h2 class="section-title">{{ t('dashboard.recentTasks') }}</h2>
      <router-link to="/tasks" class="btn btn-ghost btn-sm">{{ t('common.viewAll') }} →</router-link>
    </div>

    <div v-if="loading" class="spinner"></div>

    <div v-else-if="tasks.length === 0" class="empty-state">
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" opacity=".3"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/></svg>
      <p>{{ t('dashboard.empty') }}</p>
    </div>

    <div v-else class="tasks-grid">
      <TaskCard
        v-for="task in tasks"
        :key="task.id"
        :task="task"
        @edit="editTask"
        @complete="completeTask"
        @reopen="reopenTask"
        @archive="archiveTask"
        @timer-update="onTimerUpdate"
      />
    </div>

    <TaskFormModal
      v-if="showForm"
      :task="editingTask"
      :projects="projects"
      @close="closeForm"
      @saved="onSaved"
    />
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import TaskCard      from '@/components/TaskCard.vue'
import TaskFormModal from '@/components/TaskFormModal.vue'
import { useAuth }   from '@/composables/useAuth'
import { useI18n }   from '@/composables/useI18n'
import api from '@/api'

export default {
  name: 'DashboardView',
  components: { TaskCard, TaskFormModal },
  setup() {
    const { currentUser } = useAuth()
    const { t }           = useI18n()
    const tasks    = ref([])
    const projects = ref([])
    const loading  = ref(true)
    const showForm = ref(false)
    const editingTask = ref(null)

    const userName = computed(() => currentUser()?.name?.split(' ')[0] || t('dashboard.there'))
    const greeting = computed(() => {
      const h = new Date().getHours()
      if (h < 12) return t('dashboard.morning')
      if (h < 17) return t('dashboard.afternoon')
      return t('dashboard.evening')
    })

    const stats = computed(() => ({
      open:        tasks.value.filter(t => t.status === 'open').length,
      in_progress: tasks.value.filter(t => t.status === 'in_progress').length,
      done:        tasks.value.filter(t => t.status === 'done').length,
      critical:    tasks.value.filter(t => t.urgency === 'critical' && t.status !== 'done').length,
    }))

    async function load() {
      loading.value = true
      try {
        const [tr, pr] = await Promise.all([
          api.getTasks({ mine: 1 }),
          api.getProjects(),
        ])
        tasks.value    = (tr.data || []).slice(0, 12)
        projects.value = pr.data || []
      } catch {} finally {
        loading.value = false
      }
    }

    async function completeTask(task) {
      try {
        await api.completeTask(task.id)
        await load()
      } catch {}
    }

    async function reopenTask(task) {
      try {
        await api.reopenTask(task.id)
        await load()
      } catch {}
    }

    async function archiveTask(task) {
      if (!confirm(t('tasks.archiveConfirm', { title: task.title }))) return
      try {
        await api.archiveTask(task.id)
        tasks.value = tasks.value.filter(t => t.id !== task.id)
      } catch {}
    }

    function editTask(task) {
      editingTask.value = task
      showForm.value = true
    }

    function closeForm() {
      showForm.value = false
      editingTask.value = null
    }

    function onSaved() { load() }

    function onTimerUpdate(payload) {
      const t = tasks.value.find(t => t.id === payload.taskId)
      if (t) {
        t.timer_seconds    = payload.timer_seconds
        t.timer_started_at = payload.timer_started_at
        if (payload.status) t.status = payload.status
      }
    }

    onMounted(load)

    return {
      tasks, projects, loading, showForm, editingTask,
      userName, greeting, stats, t,
      completeTask, reopenTask, archiveTask, editTask, closeForm, onSaved, onTimerUpdate,
    }
  },
}
</script>

<style scoped>
.page-sub { color: var(--muted); font-size: 13px; margin-top: 2px; }

/* Flat stat strip — one bordered block divided by hairlines (no boxes, no icons) */
.stat-strip {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  background: var(--surface);
  overflow: hidden;
  margin-bottom: 30px;
}
.stat { padding: 16px 20px; }
.stat + .stat { border-inline-start: 1px solid var(--border); }
.stat-value {
  font-family: var(--font-mono);
  font-variant-numeric: tabular-nums;
  font-size: 30px; font-weight: 600; line-height: 1; letter-spacing: -.02em;
}
.stat-crit .stat-value { color: var(--danger); }
.stat-label { font-size: 11px; color: var(--muted); margin-top: 7px; }

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}
.section-title { font-size: 16px; font-weight: 600; }

.tasks-grid {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

@media (max-width: 900px) {
  .stat-strip { grid-template-columns: repeat(2, 1fr); }
  .stat:nth-child(3) { border-inline-start: none; }
}
@media (max-width: 600px) {
  .tasks-grid { grid-template-columns: 1fr; }
}
</style>
